import React from 'react';
import { useTranslation } from 'react-i18next';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import type { Keyword, KeywordGroup, KeywordStatus } from '@brand24-original/llm-types';
import { useUpdateKeywordStatus } from '../../../../shared/api/hooks/monitor/keyword/useUpdateKeywordStatus';
import { useSetBrandGroup } from '../../../../shared/api/hooks/monitor/keyword/useSetBrandGroup';
import { useDetachBrand } from '../../../../shared/api/hooks/monitor/keyword/useDetachBrand';
import SimpleBar from 'simplebar-react';
import BrandIcon from '../../../../shared/components/brand-icon/BrandIcon';
import '../brand-dialog/BrandList.scss';
import './KeywordAliasesCell.scss';

interface BrandNamesEditorProps {
  projectId: number;
  group: KeywordGroup;
  /** Domena marki — źródło favicona; bez niej zostaje kolorowa literka. */
  domain?: string;
  /** Kolor marki z palety raportu — tło literki, gdy favicona nie ma. */
  color?: string;
  /**
   * Marki, które można DOŁĄCZYĆ pod tę markę. `normalizedKeyword` = główna nazwa marki — po niej
   * backend przenosi całą grupę razem z wariantami.
   */
  targets: Array<{ normalizedKeyword: string; name: string }>;
  /**
   * Nazwa, której nie wolno WYŁĄCZYĆ: ostatnia aktywna nazwa marki projektu. Odłączyć ją wolno — to jedyna
   * droga powrotu, gdy trafiła pod inną markę, a odłączenie przywraca jej własny klucz w statystykach.
   */
  isLocked: (keyword: Keyword) => boolean;
  onError: () => void;
}

/**
 * Zakładka „Śledzone nazwy” w oknie marki: własne nazwy marki (przełącznik statusu, rozdzielanie),
 * marki dołączone pod nią (każda jako całość — odłączana jednym przyciskiem) i dołączanie kolejnej.
 *
 * Wszystko jest LOKALNE dla projektu (`keyword_alias_override`) — globalny słownik `keyword_aliases`
 * zostaje nietknięty. Status leci per nazwa (`PATCH /monitor/keywords/:id/status`).
 */
export const BrandNamesEditor: React.FC<BrandNamesEditorProps> = ({
  projectId,
  group,
  domain,
  color,
  targets,
  isLocked,
  onError,
}) => {
  const { t } = useTranslation();
  const updateStatus = useUpdateKeywordStatus(projectId, { onError });
  const setGroup = useSetBrandGroup(projectId, { onError });
  const detach = useDetachBrand(projectId, { onError });

  // `KeywordGroup.aliases` to POZOSTAŁE nazwy — główna siedzi na samym `group`. Nazwy, które przyszły
  // z dołączoną marką (`attachedFrom`), idą do jej karty, a nie na listę własnych nazw.
  const ownNames: Keyword[] = [group, ...group.aliases.filter((alias) => !alias.attachedFrom)];
  const attachedBrands = new Map<string, Keyword[]>();
  for (const alias of group.aliases) {
    if (alias.attachedFrom) {
      attachedBrands.set(alias.attachedFrom, [...(attachedBrands.get(alias.attachedFrom) ?? []), alias]);
    }
  }

  const statusSwitch = (name: Keyword) => (
    <InputSwitch
      className="input-switch--sm"
      tooltip={isLocked(name) ? t('brandLastActiveName') : undefined}
      tooltipOptions={{ position: 'left', showOnDisabled: true }}
      checked={name.status === 'ACTIVE'}
      disabled={isLocked(name) || updateStatus.isPending}
      onChange={() => {
        if (isLocked(name)) return;
        const next: KeywordStatus = name.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        updateStatus.mutate({ keywordId: name.id, status: next });
      }}
    />
  );

  const nameCell = (name: Keyword, isMain = false) => (
    <>
      <BrandIcon name={name.keyword} domain={domain} color={color} size="xs" />
      <span className="brand-list__name">
        <span className="brand-list__name-text">{name.keyword}</span>
        {isMain && <span className="keyword-aliases__main">{t('brandMainName')}</span>}
      </span>
    </>
  );

  return (
    <div className="keyword-aliases__panel">
      <div className="brand-list">
        <SimpleBar style={{ maxHeight: 264 }} autoHide={false}>
          {ownNames.map((name) => {
            // Głównej nazwy nie ma od czego odpinać — to ona JEST marką w tej grupie.
            const isMain = name.normalizedKeyword === group.normalizedKeyword;
            return (
              <div key={name.id} className="brand-list__row">
                {nameCell(name, isMain)}
                {statusSwitch(name)}
                {/* Główna nazwa marki odpiętej od słownika dostaje drogę powrotną — bez tego
                    rozdzielenie byłoby jednokierunkowe. */}
                {isMain && group.detachedFrom ? (
                  <button
                    type="button"
                    className="brand-list__icon-action brand-list__icon-action--outlined"
                    disabled={setGroup.isPending}
                    title={t('brandMergeTooltip', { brand: group.detachedFrom })}
                    aria-label={t('brandMerge')}
                    onClick={() =>
                      setGroup.mutate({
                        projectId,
                        normalizedKeyword: name.normalizedKeyword,
                        canonical: null,
                      })
                    }
                  >
                    <span className="ico ico--merge ico--xs" aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="brand-list__icon-action brand-list__icon-action--outlined"
                    disabled={isMain || setGroup.isPending}
                    title={isMain ? t('brandSplitMain') : t('brandSplitTooltip')}
                    aria-label={t('brandSplit')}
                    onClick={() =>
                      setGroup.mutate({
                        projectId,
                        normalizedKeyword: name.normalizedKeyword,
                        canonical: name.normalizedKeyword,
                      })
                    }
                  >
                    <span className="ico ico--split ico--xs" aria-hidden="true" />
                  </button>
                )}
              </div>
            );
          })}
        </SimpleBar>
      </div>

      {attachedBrands.size > 0 && (
        <div className="keyword-aliases__section">
          <span className="keyword-aliases__section-title">{t('brandAttachedTitle')}</span>
          <span className="keyword-aliases__section-desc">{t('brandAttachedDesc')}</span>
          {Array.from(attachedBrands.entries()).map(([attachedFrom, names]) => {
            const brandName =
              names.find((name) => name.normalizedKeyword === attachedFrom)?.keyword ?? attachedFrom;
            return (
              <div key={attachedFrom} className="brand-list keyword-aliases__attached">
                <div className="brand-list__row keyword-aliases__attached-head">
                  <span className="brand-list__name">
                    <span className="brand-list__name-text">{brandName}</span>
                    <span className="keyword-aliases__count">{names.length}</span>
                  </span>
                  <button
                    type="button"
                    className="brand-list__icon-action brand-list__icon-action--outlined"
                    disabled={detach.isPending}
                    title={t('brandDetachTooltip', { brand: brandName })}
                    aria-label={t('brandDetach')}
                    onClick={() => detach.mutate({ projectId, attachedFrom })}
                  >
                    <span className="ico ico--split ico--xs" aria-hidden="true" />
                  </button>
                </div>
                {names.map((name) => (
                  <div key={name.id} className="brand-list__row">
                    {nameCell(name)}
                    {statusSwitch(name)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Dołączenie: WYBRANA marka (cała, z wariantami) trafia pod TĘ markę — ta zostaje sobą,
          z własną nazwą i kluczem. */}
      {targets.length > 0 && (
        <div className="keyword-aliases__attach">
          <div className="keyword-aliases__attach-text">
            <span className="keyword-aliases__attach-title">{t('brandAttachTitle')}</span>
            <span className="keyword-aliases__attach-desc">{t('brandAttachDesc')}</span>
          </div>
          <Dropdown
            className="dropdown dropdown--btn-sm keyword-aliases__attach-dropdown"
            panelClassName="keyword-aliases__attach-panel"
            // Pole `value`, nie `optionValue`: Dropdown z PrimeReact 10.7 czyta `props.OptionValue`
            // (literówka), więc `optionValue` ignoruje i oddaje cały obiekt opcji.
            options={targets.map((target) => ({ name: target.name, value: target.normalizedKeyword }))}
            optionLabel="name"
            value={null}
            filter
            disabled={setGroup.isPending}
            placeholder={t('brandAttach')}
            onChange={(event) =>
              setGroup.mutate({ projectId, normalizedKeyword: event.value, canonical: group.canonical })
            }
          />
        </div>
      )}
    </div>
  );
};

export default BrandNamesEditor;
